<template>
  <div class="dashboard-page">
    <div class="dashboard-inner">
      <div class="dashboard-header">
        <h1>My En Pointe Dashboard</h1>
        <p class="user-email">{{ userEmail }}</p>
      </div>

      <div v-if="loading" class="loading-wrap">
        <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
      </div>

      <div v-else>
        <!-- Active application CTA -->
        <div v-if="activeCohort && !hasActiveApplication" class="apply-cta">
          <h2>Applications are open!</h2>
          <p>{{ activeCohort.name }} cohort is accepting applications now.</p>
          <router-link to="/pointe/apply" class="btn-primary">Apply Now →</router-link>
        </div>

        <!-- Applications -->
        <section class="dashboard-section">
          <h2>My Applications</h2>

          <div v-if="applications.length === 0" class="empty-card">
            <p>You haven't applied to a cohort yet.</p>
            <router-link to="/pointe/apply" class="link-cta">Apply when applications open →</router-link>
          </div>

          <div v-else class="card-list">
            <div v-for="app in applications" :key="app.id" class="app-card">
              <div class="card-header">
                <div>
                  <h3>{{ app.cohortName }}</h3>
                  <p class="card-date">Applied {{ formatDate(app.createdAt) }}</p>
                </div>
                <span class="status-badge" :class="app.status">{{ statusLabel(app.status) }}</span>
              </div>

              <div class="card-body">
                <div class="card-meta">
                  <span class="meta-item">
                    Payment: <strong>{{ paymentLabel(app.paymentStatus) }}</strong>
                  </span>
                  <span v-if="app.memberDiscountApplied" class="meta-item member-badge">Member discount applied</span>
                </div>

                <!-- Status-specific messaging -->
                <div v-if="app.status === 'awaiting_video'" class="status-message info">
                  <strong>Next step:</strong> Upload your video on Dropbox, then return to confirm.
                  <router-link :to="'/pointe/apply'" class="status-link">Continue Application →</router-link>
                </div>

                <div v-else-if="app.status === 'submitted'" class="status-message info">
                  Your application is under review. We'll email you when it's complete.
                </div>

                <div v-else-if="app.status === 'under_review'" class="status-message info">
                  We're reviewing your application now. Hang tight!
                </div>

                <div v-else-if="app.status === 'approved'" class="status-message success">
                  Congratulations! You've been approved.
                  <router-link :to="'/pointe/application/' + app.id" class="status-link">View Results & Next Steps →</router-link>
                </div>

                <div v-else-if="app.status === 'needs_work'" class="status-message needs-work">
                  Keep working! We've given you personalized feedback.
                  <router-link :to="'/pointe/application/' + app.id" class="status-link">View Feedback →</router-link>
                </div>
              </div>

              <div class="card-actions">
                <router-link
                  v-if="app.status === 'approved' || app.status === 'needs_work'"
                  :to="'/pointe/application/' + app.id"
                  class="btn-outline"
                >View Results</router-link>
              </div>
            </div>
          </div>
        </section>

        <!-- Consultations -->
        <section class="dashboard-section">
          <h2>My Foot 360 Consultations</h2>

          <div v-if="consultations.length === 0" class="empty-card">
            <p>No consultations on record.</p>
            <p class="empty-note">A Foot 360 Consultation is the first step before applying to the cohort.</p>
          </div>

          <div v-else class="card-list">
            <div v-for="c in consultations" :key="c.id" class="consultation-card">
              <div class="card-header">
                <div>
                  <h3>Foot 360 Consultation</h3>
                  <p class="card-date">{{ formatDate(c.consultationDate) }}</p>
                </div>
                <div class="followup-indicator">
                  <span class="followup-count">{{ c.followUpsRemaining }}</span>
                  <span class="followup-label">follow-ups left</span>
                </div>
              </div>

              <div class="card-body">
                <div class="card-meta">
                  <span v-if="c.overallApproved === true" class="result-approved">Overall: Approved ✓</span>
                  <span v-else-if="c.overallApproved === false" class="result-needs-work">Overall: Keep working</span>
                  <span v-else class="result-pending">Overall: Pending</span>
                </div>
                <div class="card-links">
                  <a v-if="c.googleDocLink" :href="c.googleDocLink" target="_blank" class="doc-link">Analysis Doc →</a>
                  <a v-if="c.loomLink" :href="c.loomLink" target="_blank" class="doc-link">Video Review →</a>
                  <a v-if="c.practiceGoogleDocLink" :href="c.practiceGoogleDocLink" target="_blank" class="doc-link">Practice Plan →</a>
                </div>
              </div>

              <div class="card-actions">
                <router-link :to="'/pointe/consultation/' + c.id" class="btn-outline">View Details</router-link>
              </div>
            </div>
          </div>
        </section>

        <!-- Self-assessment CTA -->
        <div class="self-assess-cta">
          <h3>Want to know where you stand?</h3>
          <p>Browse all en pointe criteria with resource links to help you prepare.</p>
          <router-link to="/pointe/self-assessment" class="btn-outline">Explore Self Assessment →</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { auth, db } from '@/firebase'

export default {
  data () {
    return {
      userEmail: '',
      applications: [],
      consultations: [],
      activeCohort: null,
      loading: true
    }
  },
  computed: {
    hasActiveApplication () {
      if (!this.activeCohort) return false
      return this.applications.some(a =>
        a.cohortId === this.activeCohort.id &&
        !['refunded', 'draft'].includes(a.status)
      )
    }
  },
  async created () {
    this.$store.commit('setLayout', 'pointe-layout')
    const user = await new Promise(resolve => {
      const unsub = auth.onAuthStateChanged(u => { unsub(); resolve(u) })
    })
    if (!user) {
      this.$router.push('/pointe/apply')
      return
    }
    this.userEmail = user.email
    const email = user.email.toLowerCase()

    const [appsSnap, consultSnap, cohortSnap] = await Promise.all([
      db.collection('pointe-applications').where('dancerEmail', '==', email).orderBy('createdAt', 'desc').get(),
      db.collection('pointe-consultations').where('dancerEmail', '==', email).orderBy('createdAt', 'desc').get(),
      db.collection('pointe-cohorts').where('status', '==', 'open').limit(1).get()
    ])

    this.applications = appsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.consultations = consultSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.activeCohort = cohortSnap.empty ? null : { id: cohortSnap.docs[0].id, ...cohortSnap.docs[0].data() }
    this.loading = false
  },
  methods: {
    formatDate (ts) {
      if (!ts) return '—'
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    statusLabel (status) {
      const map = {
        draft: 'Draft', pending_payment: 'Pending Payment',
        awaiting_video: 'Awaiting Video', submitted: 'Submitted',
        under_review: 'Under Review', approved: 'Approved',
        needs_work: 'Needs Work', refunded: 'Refunded'
      }
      return map[status] || status
    },
    paymentLabel (ps) {
      const map = { unpaid: 'Unpaid', paid: 'Paid', free_follow_up: 'Free Follow-up', refunded: 'Refunded' }
      return map[ps] || ps
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  min-height: 80vh;
  background: #faf9f7;
  font-family: 'Fanwood Text', Georgia, serif;
  padding: 48px 24px;
}

.dashboard-inner { max-width: 800px; margin: 0 auto; }

.dashboard-header { margin-bottom: 36px; }

.dashboard-header h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 32px;
  color: #1a1a1a;
  margin: 0 0 6px;
}

.user-email { font-size: 14px; color: #888; margin: 0; }

.loading-wrap { padding: 60px; text-align: center; }

/* Apply CTA */
.apply-cta {
  background: #1a1a1a;
  color: #e8e5e1;
  border-radius: 4px;
  padding: 28px;
  margin-bottom: 36px;
}

.apply-cta h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 22px;
  margin: 0 0 8px;
  color: #fff;
}

.apply-cta p { font-size: 15px; color: #ccc; margin: 0 0 16px; }

/* Sections */
.dashboard-section { margin-bottom: 40px; }

.dashboard-section h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 22px;
  color: #1a1a1a;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e5e1;
}

.empty-card {
  background: #fff;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 24px;
  text-align: center;
  font-size: 15px;
  color: #888;
}

.empty-note { font-size: 13px; color: #aaa; margin: 6px 0 0; }

.link-cta { color: #1a1a1a; font-size: 14px; }

/* Cards */
.card-list { display: flex; flex-direction: column; gap: 16px; }

.app-card, .consultation-card {
  background: #fff;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 20px 24px;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}

.card-header h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 18px;
  color: #1a1a1a;
  margin: 0 0 4px;
}

.card-date { font-size: 13px; color: #888; margin: 0; }

.card-body { margin-bottom: 16px; }

.card-meta { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; margin-bottom: 10px; }

.meta-item { font-size: 14px; color: #555; }

.member-badge {
  background: #e8eaf6;
  color: #283593;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}

.status-message {
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
}

.status-message.info { background: #e3f2fd; color: #1565c0; }
.status-message.success { background: #e8f5e9; color: #2e7d32; }
.status-message.needs-work { background: #fce4ec; color: #c62828; }

.status-link { display: block; margin-top: 6px; font-size: 13px; color: inherit; font-weight: 600; }

.card-actions { display: flex; gap: 10px; }

/* Follow-up indicator */
.followup-indicator { text-align: center; }

.followup-count {
  display: block;
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
}

.followup-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: .05em; }

.result-approved { color: #2e7d32; font-weight: 600; font-size: 14px; }
.result-needs-work { color: #c62828; font-weight: 600; font-size: 14px; }
.result-pending { color: #888; font-size: 14px; }

.card-links { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 8px; }
.doc-link { font-size: 13px; color: #1a1a1a; text-decoration: underline; }

/* Self-assess CTA */
.self-assess-cta {
  background: #e8e5e1;
  border-radius: 4px;
  padding: 28px;
  text-align: center;
}

.self-assess-cta h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 20px;
  color: #1a1a1a;
  margin: 0 0 8px;
}

.self-assess-cta p { font-size: 15px; color: #555; margin: 0 0 16px; }

/* Badges */
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
  white-space: nowrap;
}

.status-badge.submitted { background: #e3f2fd; color: #1565c0; }
.status-badge.under_review { background: #fff8e1; color: #f57f17; }
.status-badge.approved { background: #e8f5e9; color: #2e7d32; }
.status-badge.needs_work { background: #fce4ec; color: #c62828; }
.status-badge.awaiting_video { background: #f3e5f5; color: #6a1b9a; }
.status-badge.pending_payment { background: #fff3e0; color: #e65100; }
.status-badge.draft { background: #f5f5f5; color: #616161; }
.status-badge.refunded { background: #f5f5f5; color: #9e9e9e; }

/* Buttons */
.btn-primary {
  display: inline-block;
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #fff;
  padding: 10px 22px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  cursor: pointer;
  border-radius: 2px;
  text-decoration: none;
}

.btn-outline {
  display: inline-block;
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 8px 18px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  cursor: pointer;
  border-radius: 2px;
  text-decoration: none;
}

.btn-outline:hover { background: #1a1a1a; color: #fff; }
</style>
